/*
 * shipping-estimate.js (patched)
 *
 * Fixes:
 *  1. The estimate used to run only once on page load. When a customer logged in
 *     without a full page reload (for example through the auction widget), the
 *     data-* attributes on #shipping-estimate stayed empty and nothing ran until
 *     a manual refresh. Now the script re-reads the address from the server
 *     (by fetching the current page) as soon as a login is detected.
 *  2. Carrier-calculated rates (FedEx / UPS / Mainfreight) are often empty on the
 *     first request. The carrier-rates query is now retried a few times before the
 *     "still loading" message is shown, and the whole flow gets one extra attempt.
 *
 * Public API: window.shippingEstimateRefresh() forces a re-check (useful to call
 * from any login callback).
 */
(function () {
  var STOREFRONT_ACCESS_TOKEN = "8f1a4a371c0e0ef05ed62f73dee295cb";
  var API_VERSION = "2025-01";

  var container = document.getElementById("shipping-estimate");
  if (!container) return;

  var shopDomain = container.getAttribute("data-shop-domain");
  var endpoint = "https://" + shopDomain + "/api/" + API_VERSION + "/graphql.json";

  var CARRIER_RETRY_DELAYS = [1500, 3000, 4500]; // ms between carrier-rate retries
  var FLOW_RETRIES = 1;                          // extra full attempts when no rates come back

  var runId = 0;          // increments per run, so stale async results are ignored
  var lastCountry = null; // country code of the last successful start
  var running = false;

  /* ---------- helpers ---------- */

  function log() {
    var args = ["[shipping-estimate]"].concat([].slice.call(arguments));
    console.log.apply(console, args);
  }

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  function getResultsEl() {
    return document.getElementById("shipping-estimate-results");
  }

  function setResults(html) {
    var el = getResultsEl();
    if (el) el.innerHTML = html;
  }

  function readAddress(el) {
    return {
      countryCode: el.getAttribute("data-country-code") || "",
      address1: el.getAttribute("data-address1") || "",
      city: el.getAttribute("data-city") || "",
      zip: el.getAttribute("data-zip") || "",
      provinceCode: el.getAttribute("data-province-code") || ""
    };
  }

  function formatMoney(amount, currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: currencyCode }).format(amount);
    } catch (e) {
      return amount + " " + currencyCode;
    }
  }

  function graphql(query, variables) {
    return fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN
      },
      body: JSON.stringify({ query: query, variables: variables })
    })
      .then(function (res) { return res.json(); })
      .then(function (json) {
        log("GraphQL response:", json);
        if (json.errors && json.errors.length) throw new Error(json.errors[0].message);
        if (!json.data) throw new Error("Empty response from Storefront API, check the token and API version.");
        return json;
      });
  }

  /* ---------- queries ---------- */

  var CART_CREATE =
    "mutation cartCreate($lines: [CartLineInput!]!, $country: CountryCode) {" +
    "  cartCreate(input: { lines: $lines, buyerIdentity: { countryCode: $country } }) {" +
    "    cart { id }" +
    "    userErrors { message }" +
    "  }" +
    "}";

  var ADD_DELIVERY_ADDRESS =
    "mutation cartDeliveryAddressesAdd($cartId: ID!, $addresses: [CartSelectableAddressInput!]!) {" +
    "  cartDeliveryAddressesAdd(cartId: $cartId, addresses: $addresses) {" +
    "    cart {" +
    "      id" +
    "      deliveryGroups(first: 5) {" +
    "        nodes {" +
    "          deliveryOptions {" +
    "            handle title description" +
    "            estimatedCost { amount currencyCode }" +
    "          }" +
    "        }" +
    "      }" +
    "    }" +
    "    userErrors { message }" +
    "  }" +
    "}";

  var CARRIER_RATES_QUERY =
    "query cartWithCarrierRates($cartId: ID!) {" +
    "  cart(id: $cartId) {" +
    "    id" +
    '    ...DeliveryGroups @defer(label: "carrierRates")' +
    "  }" +
    "}" +
    "fragment DeliveryGroups on Cart {" +
    "  deliveryGroups(first: 5, withCarrierRates: true) {" +
    "    nodes {" +
    "      deliveryOptions {" +
    "        handle title description" +
    "        estimatedCost { amount currencyCode }" +
    "      }" +
    "    }" +
    "  }" +
    "}";

  /* ---------- multipart/mixed parsing (for @defer) ---------- */

  function parseMultipartMixed(text) {
    var payloads = [];
    text.split("--graphql").forEach(function (part) {
      part = part.trim();
      if (!part || part === "--") return;
      var splitAt = part.indexOf("\r\n\r\n");
      if (splitAt === -1) splitAt = part.indexOf("\n\n");
      var body = splitAt === -1 ? part : part.slice(splitAt).trim();
      try { payloads.push(JSON.parse(body)); } catch (e) { /* ignore non-JSON parts */ }
    });
    return payloads;
  }

  function countOptions(groups) {
    var n = 0;
    (groups || []).forEach(function (g) { n += (g.deliveryOptions || []).length; });
    return n;
  }

  /* ---------- carrier rates with retry ---------- */

  function fetchCarrierRatesOnce(cartId) {
    return fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "multipart/mixed",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN
      },
      body: JSON.stringify({ query: CARRIER_RATES_QUERY, variables: { cartId: cartId } })
    })
      .then(function (res) { return res.text(); })
      .then(function (text) {
        var payloads = parseMultipartMixed(text);
        log("carrier-rates payloads:", payloads);
        var groups = null;
        payloads.forEach(function (payload) {
          if (payload.data && payload.data.cart && payload.data.cart.deliveryGroups) {
            groups = payload.data.cart.deliveryGroups.nodes;
          }
          (payload.incremental || []).forEach(function (chunk) {
            if (chunk.data && chunk.data.deliveryGroups) groups = chunk.data.deliveryGroups.nodes;
          });
        });
        return groups;
      });
  }

  // Retries until at least one option comes back, then falls back to the flat rates.
  function fetchCarrierRates(cartId, fallbackGroups, myRun) {
    var attempt = 0;

    function tryOnce() {
      return fetchCarrierRatesOnce(cartId)
        .catch(function (err) {
          log("carrier-rates fetch failed:", err);
          return null;
        })
        .then(function (groups) {
          if (myRun !== runId) return null; // a newer run took over
          if (countOptions(groups) > 0) return groups;
          if (attempt < CARRIER_RETRY_DELAYS.length) {
            var delay = CARRIER_RETRY_DELAYS[attempt++];
            log("no carrier rates yet, retrying in " + delay + "ms (attempt " + attempt + ")");
            return wait(delay).then(tryOnce);
          }
          return countOptions(fallbackGroups) > 0 ? fallbackGroups : null;
        });
    }

    return tryOnce();
  }

  /* ---------- rendering ---------- */

  function renderOptions(deliveryGroups) {
    var options = [];
    (deliveryGroups || []).forEach(function (group) {
      (group.deliveryOptions || []).forEach(function (opt) { options.push(opt); });
    });

    if (!options.length) return false;

    var html = "<ul>";
    options.forEach(function (opt) {
      var price = formatMoney(opt.estimatedCost.amount, opt.estimatedCost.currencyCode);
      html += "<li><span>" + (opt.title || opt.description || "Shipping") + "</span><strong>" + price + "</strong></li>";
    });
    html += "</ul>";
    setResults(html);
    return true;
  }

  function renderNoRatesMessage() {
    setResults(
      '<p class="shipping-estimate__error">We could not load the shipping costs right now. ' +
      'Please refresh the page to try again. We ship to all EU countries.</p>'
    );
  }

  /* ---------- main flow ---------- */

  function calculate(addr, myRun) {
    var variantId = container.getAttribute("data-variant-id");

    if (!variantId) {
      setResults('<p class="shipping-estimate__error">This product has no purchasable variant.</p>');
      return Promise.resolve();
    }
    if (STOREFRONT_ACCESS_TOKEN.indexOf("PASTE_") === 0) {
      setResults('<p class="shipping-estimate__error">Storefront API token not configured yet (see shipping-estimate.js).</p>');
      return Promise.resolve();
    }

    var variantGid = "gid://shopify/ProductVariant/" + variantId;

    function attemptFlow(retriesLeft) {
      setResults(
        '<p class="shipping-estimate__hint"><span class="shipping-estimate__spinner" aria-hidden="true"></span> Calculating shipping…</p>'
      );

      return graphql(CART_CREATE, {
        lines: [{ merchandiseId: variantGid, quantity: 1 }],
        country: addr.countryCode
      })
        .then(function (res) {
          var errors = res.data && res.data.cartCreate && res.data.cartCreate.userErrors;
          if (errors && errors.length) throw new Error(errors[0].message);
          var cartId = res.data.cartCreate.cart.id;

          var deliveryAddress = { countryCode: addr.countryCode };
          if (addr.address1) deliveryAddress.address1 = addr.address1;
          if (addr.city) deliveryAddress.city = addr.city;
          if (addr.zip) deliveryAddress.zip = addr.zip;
          if (addr.provinceCode) deliveryAddress.provinceCode = addr.provinceCode;

          return graphql(ADD_DELIVERY_ADDRESS, {
            cartId: cartId,
            addresses: [{ address: { deliveryAddress: deliveryAddress }, selected: true, oneTimeUse: true }]
          });
        })
        .then(function (res) {
          var errors = res.data && res.data.cartDeliveryAddressesAdd && res.data.cartDeliveryAddressesAdd.userErrors;
          if (errors && errors.length) throw new Error(errors[0].message);
          var cart = res.data.cartDeliveryAddressesAdd.cart;
          return fetchCarrierRates(cart.id, cart.deliveryGroups.nodes, myRun);
        })
        .then(function (groups) {
          if (myRun !== runId) return; // superseded by a newer run
          if (renderOptions(groups)) return;
          if (retriesLeft > 0) {
            log("still no rates, restarting the flow (" + retriesLeft + " retries left)");
            return wait(2000).then(function () {
              if (myRun !== runId) return;
              return attemptFlow(retriesLeft - 1);
            });
          }
          renderNoRatesMessage();
        });
    }

    return attemptFlow(FLOW_RETRIES).catch(function (err) {
      if (myRun !== runId) return;
      setResults('<p class="shipping-estimate__error">Could not load shipping options (' + err.message + ").</p>");
    });
  }

  function start(addr) {
    if (!addr.countryCode) return Promise.resolve();
    if (!getResultsEl()) {
      // Logged-out markup has no results element, so create one.
      container.innerHTML = '<div id="shipping-estimate-results" class="shipping-estimate__results"></div>';
    }
    var myRun = ++runId;
    running = true;
    lastCountry = addr.countryCode;
    return calculate(addr, myRun).then(function () {
      if (myRun === runId) running = false;
    });
  }

  /* ---------- picking up the address after an Ajax login ---------- */

  // Re-fetches the current page, reads the freshly rendered data-* attributes
  // (Liquid knows the logged-in customer) and applies them to the live DOM.
  function refreshFromServer() {
    return fetch(window.location.href, { credentials: "same-origin", headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var fresh = doc.getElementById("shipping-estimate");
        if (!fresh) return;

        ["data-country-code", "data-address1", "data-city", "data-zip", "data-province-code"].forEach(function (attr) {
          container.setAttribute(attr, fresh.getAttribute(attr) || "");
        });

        // Bring over the results container (the server renders it only when a country is known).
        if (!getResultsEl()) {
          var freshResults = doc.getElementById("shipping-estimate-results");
          if (freshResults) {
            container.innerHTML = freshResults.outerHTML;
          }
        }

        // Update the "Sign in to see shipping details" line if present.
        var oldNote = document.querySelector("p.shipping-country:not(:first-of-type)");
        var freshNotes = doc.querySelectorAll("p.shipping-country");
        if (oldNote && freshNotes.length > 1) oldNote.outerHTML = freshNotes[freshNotes.length - 1].outerHTML;

        var addr = readAddress(container);
        if (addr.countryCode && addr.countryCode !== lastCountry) return start(addr);
      })
      .catch(function (err) { log("refreshFromServer failed:", err); });
  }

  function maybeRefresh() {
    if (running) return;
    if (readAddress(container).countryCode) return; // already have a country
    refreshFromServer();
  }

  // Public hook: call this from any login callback.
  window.shippingEstimateRefresh = function () {
    return refreshFromServer();
  };

  /* ---------- boot ---------- */

  var initial = readAddress(container);
  if (initial.countryCode) {
    start(initial);
  } else {
    log("no country yet (logged out, or login not reflected in the DOM). Waiting for login.");
  }

  // 1. The auction widget exposes the customer id on #wk-auction[cust-id]. Watch for it.
  var wk = document.getElementById("wk-auction");
  if (wk && window.MutationObserver) {
    new MutationObserver(function () {
      if (wk.getAttribute("cust-id")) maybeRefresh();
    }).observe(wk, { attributes: true, attributeFilter: ["cust-id"] });
  }

  // 2. Generic fallbacks: returning to the tab (after logging in in another tab or popup)
  //    and Shopify customer events, if the theme dispatches them.
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") maybeRefresh();
  });
  window.addEventListener("focus", maybeRefresh);
  document.addEventListener("customer:login", maybeRefresh);
  document.addEventListener("customer:loggedin", maybeRefresh);
})();